import {
	type QueryClient,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useTenantSwitcher } from "@/features/tenant-switcher";
import { apiFetch } from "@/lib/auth-api";
import { type MvBoundarySpec, mvStaleTimeWithLiveTtl } from "@/lib/mv-stale-time";
import {
	type AgentSyncJob,
	type BindingInfo,
	type CatalogEntry,
	type ConnectorInput,
	type ConnectorSaveResponse,
	type DeviceBindingRow,
	type JobAccepted,
	type KeyCheck,
	type KeyCheckDetail,
	type KeyCheckInput,
	type KeyCheckRange,
	type KeyClass,
	type Level3,
	type Level3Input,
	type ManualCheck,
	type ManualCheckAccepted,
	type ManualCheckCost,
	type ManualCheckDue,
	type ManualCheckInput,
	type ManualCheckItem,
	type ManualChecksResponse,
	type PharmacyDetailResponse,
	type PharmacyListParams,
	type PharmacyListResponse,
	type RepairPreviewResponse,
	type RepairsResponse,
	type RepairTarget,
	type SyncSettings,
	type SyncSettingsInput,
	TERMINAL_JOB_STATUSES,
	type VerifySqlKind,
	type VerifySqlResponse,
} from "./types";

/** Strona listy w tabeli (infinite scroll). Kontrakt: domyslnie 50, max 500. */
export const PHARMACY_PAGE_SIZE = 100;
/** Strona przy eksporcie calego wyniku — maksimum kontraktu. */
export const PHARMACY_EXPORT_PAGE_SIZE = 500;

// Kadencja daemonu agent-sync (AGENT_SYNC_F1.md §2): godzinowy przebieg startuje
// o :47 UTC, apteki rusza rozlozone hashem idfirm na 30 min, wiec komplet cyklu
// jest w bazie ok. :20 nastepnej godziny. Nocny (22:31 UTC) liczy 35 dni + poziom 2.
// Cache czeka do granicy :20 (ten sam mechanizm co godzinowe MV w mv-stale-time),
// ale nie dluzej niz 15 min — wyniki pojedynczych aptek splywaja w oknie :47–:20,
// a raport sluzy wlasnie do sprawdzenia, czy apteka juz sie dogonila.
const AGENT_SYNC_HOURLY: MvBoundarySpec = { kind: "hourly", freshAtMinute: 20 };
const AGENT_SYNC_LIVE_TTL_MS = 15 * 60_000;
export const agentSyncStaleTime = mvStaleTimeWithLiveTtl(AGENT_SYNC_HOURLY, AGENT_SYNC_LIVE_TTL_MS);

export const AGENT_SYNC_QUERY_KEY = ["agent-sync"] as const;

export const agentSyncKeys = {
	pharmacies: (tenantId: number | null | undefined, params: PharmacyListParams) =>
		[...AGENT_SYNC_QUERY_KEY, "pharmacies", tenantId ?? null, params] as const,
	pharmacy: (
		tenantId: number | null | undefined,
		idfirm: number,
		from: string | undefined,
		to: string | undefined,
		scope: string | undefined,
	) =>
		[
			...AGENT_SYNC_QUERY_KEY,
			"pharmacy",
			tenantId ?? null,
			idfirm,
			from ?? null,
			to ?? null,
			scope ?? null,
		] as const,
	job: (tenantId: number | null | undefined, jobId: number, scope?: string) =>
		[...AGENT_SYNC_QUERY_KEY, "job", tenantId ?? null, jobId, scope ?? null] as const,
	deviceBindings: (tenantId: number | null | undefined, scope?: string) =>
		[...AGENT_SYNC_QUERY_KEY, "device-bindings", tenantId ?? null, scope ?? null] as const,
	catalog: (tenantId: number | null | undefined) =>
		[...AGENT_SYNC_QUERY_KEY, "catalog", tenantId ?? null] as const,
	manualChecks: (tenantId: number | null | undefined, scope: string | undefined) =>
		[...AGENT_SYNC_QUERY_KEY, "manual-checks", tenantId ?? null, scope ?? null] as const,
	manualCheckItems: (tenantId: number | null | undefined, id: number, scope: string | undefined) =>
		[...AGENT_SYNC_QUERY_KEY, "manual-check-items", tenantId ?? null, id, scope ?? null] as const,
	manualCheckCost: (tenantId: number | null | undefined) =>
		[...AGENT_SYNC_QUERY_KEY, "manual-check-cost", tenantId ?? null] as const,
	manualCheckDue: (
		tenantId: number | null | undefined,
		scope: string | undefined,
		input: ManualCheckInput,
	) =>
		[...AGENT_SYNC_QUERY_KEY, "manual-check-due", tenantId ?? null, scope ?? null, input] as const,
	repairPreview: (
		tenantId: number | null | undefined,
		idfirms: readonly number[],
		scope: string | undefined,
	) =>
		[...AGENT_SYNC_QUERY_KEY, "repair-preview", tenantId ?? null, idfirms, scope ?? null] as const,
	/** D72: listy kluczy apteki dla tabeli (najnowsze pierwsze), opcjonalnie obejmujące zakres. */
	keyChecks: (
		tenantId: number | null | undefined,
		idfirm: number,
		table: string,
		scope: string | undefined,
		range?: KeyCheckRange,
	) =>
		[
			...AGENT_SYNC_QUERY_KEY,
			"key-checks",
			tenantId ?? null,
			idfirm,
			table,
			scope ?? null,
			range?.dateFrom ?? null,
			range?.dateTo ?? null,
		] as const,
	/** D72: klucze zlecenia — `cls` zawęża je po stronie serwera (`?class=`). */
	keyCheck: (
		tenantId: number | null | undefined,
		idfirm: number,
		id: number,
		cls: KeyClass | null,
		scope: string | undefined,
	) =>
		[
			...AGENT_SYNC_QUERY_KEY,
			"key-check",
			tenantId ?? null,
			idfirm,
			id,
			cls,
			scope ?? null,
		] as const,
	/** F3: najnowszy poziom 3 apteki (dla listy kluczy `checkId` albo dowolny). */
	level3: (
		tenantId: number | null | undefined,
		idfirm: number,
		checkId: number | null,
		scope: string | undefined,
	) =>
		[...AGENT_SYNC_QUERY_KEY, "level3", tenantId ?? null, idfirm, checkId, scope ?? null] as const,
	/** SQL weryfikacji rozjazdu (A·S·T) dla tabeli i zakresu karty. */
	verifySql: (
		tenantId: number | null | undefined,
		idfirm: number,
		params: VerifySqlParams,
		scope: string | undefined,
	) =>
		[
			...AGENT_SYNC_QUERY_KEY,
			"verify-sql",
			tenantId ?? null,
			idfirm,
			params,
			scope ?? null,
		] as const,
};

export interface VerifySqlParams {
	table: string;
	from: string;
	to: string;
	kind: VerifySqlKind;
}

/**
 * Stan usługi zmienia się z heartbeatem (60 s) i `sync_state` (≤ 5 min), nie z
 * kadencją godzinową porównania — kafelek urządzenia i karta czytają świeższe dane.
 */
const DEVICE_BINDINGS_STALE_MS = 60_000;
/** Polling joba (test połączenia, przeliczenie): co 2 s, najwyżej 2 min. */
export const JOB_POLL_INTERVAL_MS = 2_000;
export const JOB_POLL_MAX_ATTEMPTS = 60;

function query(params: Record<string, string | number | undefined>): string {
	const qs = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== "") qs.set(key, String(value));
	}
	const s = qs.toString();
	return s ? `?${s}` : "";
}

export const agentSyncApi = {
	listPharmacies: (params: PharmacyListParams, page: number, pageSize: number) =>
		apiFetch<PharmacyListResponse>(
			`/agent-sync/pharmacies${query({
				scope: params.scope,
				sortBy: params.sortBy,
				sortDir: params.sortBy ? params.sortDir : undefined,
				columnFilters: params.columnFilters,
				page,
				pageSize,
			})}`,
		),
	// `scope` z listy idzie też do panelu apteki: backend sprawdza aptekę w zakresie
	// ∩ widoczności (spoza zakresu = 403), więc panel nie wychodzi poza widok listy.
	// `compact`: panel nie pokazuje historii ani stron dnia — karta bez nich (879605:
	// 1,86 MB → kilkadziesiąt KB, a w trakcie sprawdzeń odświeża się co 10 s).
	getPharmacy: (idfirm: number, from?: string, to?: string, scope?: string) =>
		apiFetch<PharmacyDetailResponse>(
			`/agent-sync/pharmacies/${idfirm}${query({ from, to, scope, compact: "true" })}`,
		),
	// `scope` listy idzie też do mutacji, jobów i bindingów: backend sprawdza
	// aptekę w zakresie ∩ widoczności tak samo jak przy karcie (brak = sama widoczność).
	// Hasło jest write-only: brak klucza `password` = bez zmiany, `""` = usuń.
	saveConnector: (idfirm: number, input: ConnectorInput, scope?: string) =>
		apiFetch<ConnectorSaveResponse>(
			`/agent-sync/pharmacies/${idfirm}/connector${query({ scope })}`,
			{
				method: "PUT",
				body: JSON.stringify(input),
			},
		),
	testConnector: (idfirm: number, scope?: string) =>
		apiFetch<JobAccepted>(`/agent-sync/pharmacies/${idfirm}/connector/test${query({ scope })}`, {
			method: "POST",
		}),
	setAwaitingCredentials: (idfirm: number, awaiting: boolean, scope?: string) =>
		apiFetch<unknown>(
			`/agent-sync/pharmacies/${idfirm}/connector/await-credentials${query({ scope })}`,
			{ method: "POST", body: JSON.stringify({ awaiting }) },
		),
	setBinding: (idfirm: number, deviceId: string, enabled: boolean, scope?: string) =>
		apiFetch<{ binding: BindingInfo | null }>(
			`/agent-sync/pharmacies/${idfirm}/binding${query({ scope })}`,
			{ method: "POST", body: JSON.stringify({ deviceId, enabled }) },
		),
	restartBinding: (idfirm: number, scope?: string) =>
		apiFetch<unknown>(`/agent-sync/pharmacies/${idfirm}/binding/restart${query({ scope })}`, {
			method: "POST",
		}),
	runNow: (idfirm: number, days?: number, scope?: string) =>
		apiFetch<JobAccepted>(`/agent-sync/pharmacies/${idfirm}/runs${query({ scope })}`, {
			method: "POST",
			body: JSON.stringify(days ? { days } : {}),
		}),
	getJob: (jobId: number, scope?: string) =>
		apiFetch<AgentSyncJob>(`/agent-sync/jobs/${jobId}${query({ scope })}`),
	listDeviceBindings: (scope?: string) =>
		apiFetch<DeviceBindingRow[]>(`/agent-sync/device-bindings${query({ scope })}`),
	// D47: pauza, preset kadencji i okno zakazane stanowiska (wymaga bindingu → 409 `binding_missing`).
	saveSyncSettings: (idfirm: number, input: SyncSettingsInput, scope?: string) =>
		apiFetch<{ settings: SyncSettings }>(
			`/agent-sync/pharmacies/${idfirm}/sync-settings${query({ scope })}`,
			{ method: "PATCH", body: JSON.stringify(input) },
		),
	// D46/D47: katalog zapytań (tylko odczyt, bez danych aptek).
	getCatalog: () => apiFetch<CatalogEntry[]>("/agent-sync/catalog"),
	// Dialog „Napraw rozjazdy”: miesiące aptek ze stanem liczonym regułami zlecenia (D64).
	getRepairPreview: (idfirms: readonly number[], scope?: string) =>
		apiFetch<RepairPreviewResponse>(
			`/agent-sync/repairs/preview${query({ scope, idfirms: idfirms.join(",") })}`,
		),
	// Ręczne zlecenie naprawy robotowi (backlog D38) — zbiorczo, wynik per pozycja.
	// Jedna ścieżka dla dialogu, karty i szczegółów dnia; `month` = `YYYY-MM-01`.
	requestRepairs: (items: readonly RepairTarget[], scope?: string) =>
		apiFetch<RepairsResponse>(`/agent-sync/repairs${query({ scope })}`, {
			method: "POST",
			body: JSON.stringify({ items }),
		}),
	// F1 §7: masowe sprawdzenie — apteki rozwiązuje backend (zakres ∩ widoczność, filtry listy).
	createManualCheck: (input: ManualCheckInput, scope?: string) =>
		apiFetch<ManualCheckAccepted>(`/agent-sync/manual-checks${query({ scope })}`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	listManualChecks: (scope?: string) =>
		apiFetch<ManualChecksResponse>(`/agent-sync/manual-checks${query({ scope })}`),
	getManualCheckItems: (id: number, scope?: string) =>
		apiFetch<{ items: ManualCheckItem[] }>(
			`/agent-sync/manual-checks/${id}/items${query({ scope })}`,
		),
	cancelManualCheck: (id: number, scope?: string) =>
		apiFetch<{ outcome: "cancelled" | "cancel_requested" }>(
			`/agent-sync/manual-checks/${id}/cancel${query({ scope })}`,
			{ method: "POST" },
		),
	getManualCheckCost: () => apiFetch<ManualCheckCost>("/agent-sync/manual-checks/cost"),
	// Podgląd zaległych kontroli: te same apteki co zlecenie, bez zapisu (POST — body jak zlecenie).
	getManualCheckDue: (input: ManualCheckInput, scope?: string) =>
		apiFetch<ManualCheckDue>(`/agent-sync/manual-checks/due${query({ scope })}`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	// D71: koniec planu historii A apteki (409 `a_history_finished` = plan już się skończył).
	cancelAHistory: (idfirm: number, scope?: string) =>
		apiFetch<{ outcome: "cancelled" }>(
			`/agent-sync/pharmacies/${idfirm}/a-history/cancel${query({ scope })}`,
			{ method: "POST" },
		),
	// D72: lista kluczy „Z czego wynika rozjazd?” — A, S i T klucz po kluczu (≤ 31 dni).
	createKeyCheck: (idfirm: number, input: KeyCheckInput, scope?: string) =>
		apiFetch<KeyCheck>(`/agent-sync/pharmacies/${idfirm}/key-checks${query({ scope })}`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	listKeyChecks: (idfirm: number, table: string, scope?: string, range?: KeyCheckRange) =>
		apiFetch<{ checks: KeyCheck[] }>(
			`/agent-sync/pharmacies/${idfirm}/key-checks${query({ scope, table, dateFrom: range?.dateFrom, dateTo: range?.dateTo })}`,
		),
	getKeyCheck: (idfirm: number, id: number, cls: KeyClass | null, scope?: string) =>
		apiFetch<KeyCheckDetail>(
			`/agent-sync/pharmacies/${idfirm}/key-checks/${id}${query({ scope, class: cls ?? undefined })}`,
		),
	// SQL weryfikacji rozjazdu: tekst do ręcznego uruchomienia, bez zapytań do baz aptek.
	getVerifySql: (idfirm: number, params: VerifySqlParams, scope?: string) =>
		apiFetch<VerifySqlResponse>(
			`/agent-sync/pharmacies/${idfirm}/verify-sql${query({ ...params, scope })}`,
		),
	// 409 `key_check_finished` = zlecenie skończyło się samo.
	cancelKeyCheck: (idfirm: number, id: number, scope?: string) =>
		apiFetch<{ outcome: "cancelled" }>(
			`/agent-sync/pharmacies/${idfirm}/key-checks/${id}/cancel${query({ scope })}`,
			{ method: "POST" },
		),
	// F3 poziom 3: wartości kolumn kluczy A/S/T — żywe zlecenia listy (automat i ręczne,
	// najnowsze najpierw), bez listy najnowsze apteki.
	latestLevel3: (idfirm: number, checkId: number | null, scope?: string) =>
		apiFetch<{ items: Level3[] }>(
			`/agent-sync/pharmacies/${idfirm}/level3${query({ scope, checkId: checkId ?? undefined })}`,
		),
	// 409: `service_too_old`, `level3_running`, `no_binding`; 400: zakres albo klucze.
	createLevel3: (idfirm: number, input: Level3Input, scope?: string) =>
		apiFetch<Level3>(`/agent-sync/pharmacies/${idfirm}/level3${query({ scope })}`, {
			method: "POST",
			body: JSON.stringify(input),
		}),
	// 409 `level3_finished`: zlecenie ma już wynik apteki.
	cancelLevel3: (idfirm: number, id: number, scope?: string) =>
		apiFetch<{ outcome: "cancelled" }>(
			`/agent-sync/pharmacies/${idfirm}/level3/${id}/cancel${query({ scope })}`,
			{ method: "POST" },
		),
};

export function usePharmacyList(params: PharmacyListParams, enabled: boolean) {
	const { selectedTenantId } = useTenantSwitcher();
	return useInfiniteQuery({
		queryKey: agentSyncKeys.pharmacies(selectedTenantId, params),
		queryFn: ({ pageParam }) => agentSyncApi.listPharmacies(params, pageParam, PHARMACY_PAGE_SIZE),
		initialPageParam: 1,
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((n, p) => n + p.rows.length, 0);
			return last.rows.length > 0 && loaded < last.total ? last.page + 1 : undefined;
		},
		enabled,
		staleTime: agentSyncStaleTime,
	});
}

/** Wszystkie strony wyniku do eksportu — `total` z tej samej serii odpowiedzi. */
export async function fetchAllPharmacies(params: PharmacyListParams) {
	const first = await agentSyncApi.listPharmacies(params, 1, PHARMACY_EXPORT_PAGE_SIZE);
	const rows = [...first.rows];
	let page = 1;
	while (rows.length < first.total) {
		page += 1;
		const next = await agentSyncApi.listPharmacies(params, page, PHARMACY_EXPORT_PAGE_SIZE);
		if (next.rows.length === 0) break;
		rows.push(...next.rows);
	}
	return { rows, totalCount: first.total };
}

export function usePharmacyDetail(
	idfirm: number,
	from: string | undefined,
	to: string | undefined,
	scope: string | undefined,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.pharmacy(selectedTenantId, idfirm, from, to, scope),
		queryFn: () => agentSyncApi.getPharmacy(idfirm, from, to, scope),
		enabled: Number.isFinite(idfirm),
		staleTime: agentSyncStaleTime,
		refetchInterval: (q) => pharmacyCardPollMs(q.state.data),
	});
}

/** Zlecenie S ↔ T apteki czeka w kolejce (godziny pracy, noc) — postęp zmienia się rzadko. */
const QUEUED_CHECK_POLL_MS = 60_000;

/**
 * D71: karta odświeża się sama, dopóki apteka liczy historię A (porcja to kilkanaście
 * sekund ERP) albo trwa jej sprawdzenie S ↔ T; zlecenie w kolejce — co minutę.
 */
export function pharmacyCardPollMs(data: PharmacyDetailResponse | undefined): number | false {
	if (data?.aHistory?.active || data?.stQueue?.status === "running") return MANUAL_CHECKS_POLL_MS;
	return data?.stQueue ? QUEUED_CHECK_POLL_MS : false;
}

/** Rozgrzewa panel apteki na hover wiersza listy — ten sam klucz co `usePharmacyDetail`. */
export function prefetchPharmacyDetail(
	queryClient: QueryClient,
	tenantId: number | null | undefined,
	idfirm: number,
	from: string | undefined,
	to: string | undefined,
	scope: string | undefined,
) {
	return queryClient.prefetchQuery({
		queryKey: agentSyncKeys.pharmacy(tenantId, idfirm, from, to, scope),
		queryFn: () => agentSyncApi.getPharmacy(idfirm, from, to, scope),
		staleTime: agentSyncStaleTime,
	});
}

// ── F2: stanowisko, konektor, joby ──────────────────────────────────────────

const keyPart = (key: readonly unknown[], i: number) => key[i];

/**
 * Po mutacji stanowiska/konektora, naprawie albo sprawdzeniu: szczegóły i przyczyny
 * rozjazdów TEJ apteki (panel, każdy zakres), lista (kolumny A, stan usługi)
 * i bindingi kafelków urządzeń.
 */
export function invalidatePharmacySync(
	queryClient: QueryClient,
	idfirm: number,
	{ withList = true }: { withList?: boolean } = {},
) {
	const ofPharmacy = (kind: string) => ({
		predicate: (q: { queryKey: readonly unknown[] }) =>
			keyPart(q.queryKey, 0) === AGENT_SYNC_QUERY_KEY[0] &&
			keyPart(q.queryKey, 1) === kind &&
			keyPart(q.queryKey, 3) === idfirm,
	});
	void queryClient.invalidateQueries(ofPharmacy("pharmacy"));
	void queryClient.invalidateQueries(ofPharmacy("problems"));
	// S|T na żywo rozwiniętej grupy — inaczej po naprawie „zgodne teraz” do końca staleTime.
	void queryClient.invalidateQueries(ofPharmacy("problems-side"));
	if (withList) invalidatePharmacyList(queryClient);
}

/** Lista aptek i powiązania urządzeń — raz na zmianę, także zbiorczą. */
function invalidatePharmacyList(queryClient: QueryClient) {
	void queryClient.invalidateQueries({ queryKey: [...AGENT_SYNC_QUERY_KEY, "pharmacies"] });
	void queryClient.invalidateQueries({ queryKey: [...AGENT_SYNC_QUERY_KEY, "device-bindings"] });
}

export function useSaveConnector(idfirm: number, scope?: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: ConnectorInput) => agentSyncApi.saveConnector(idfirm, input, scope),
		onSuccess: () => invalidatePharmacySync(queryClient, idfirm),
	});
}

export function useTestConnector(idfirm: number, scope?: string) {
	return useMutation({ mutationFn: () => agentSyncApi.testConnector(idfirm, scope) });
}

export function useSetAwaitingCredentials(idfirm: number, scope?: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (awaiting: boolean) => agentSyncApi.setAwaitingCredentials(idfirm, awaiting, scope),
		onSuccess: () => invalidatePharmacySync(queryClient, idfirm),
	});
}

/**
 * Włącz/wyłącz stanowisko — karta apteki (z `scope` listy) i kafelek urządzenia
 * (bez zakresu: backend sprawdza wtedy samą widoczność).
 */
export function useSetBinding(scope?: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (v: { idfirm: number; deviceId: string; enabled: boolean }) =>
			agentSyncApi.setBinding(v.idfirm, v.deviceId, v.enabled, scope),
		onSuccess: (_data, v) => invalidatePharmacySync(queryClient, v.idfirm),
	});
}

export function useRestartBinding(scope?: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (idfirm: number) => agentSyncApi.restartBinding(idfirm, scope),
		onSuccess: (_data, idfirm) => invalidatePharmacySync(queryClient, idfirm),
	});
}

export function useRunNow(idfirm: number, scope?: string) {
	return useMutation({
		mutationFn: (days?: number) => agentSyncApi.runNow(idfirm, days, scope),
	});
}

/** D47: zapis ustawień stanowiska — odświeża kartę (odznaka pauzy) i listę (`paused`). */
export function useSaveSyncSettings(idfirm: number, scope?: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: SyncSettingsInput) => agentSyncApi.saveSyncSettings(idfirm, input, scope),
		onSuccess: () => invalidatePharmacySync(queryClient, idfirm),
	});
}

/**
 * Zlecenie wchodzi do backlogu od razu, ale Robot przetwarza je w swoim przebiegu
 * (poll co 60 s) — podgląd odświeżany co minutę, gdy coś czeka na odpowiedź.
 */
export const REPAIR_PREVIEW_POLL_MS = 60_000;

/**
 * Stan napraw aptek (dialog „Napraw rozjazdy”, kroki w panelu bocznym). Krótki
 * cache: zlecenia czekają na Robota minutami, a nie na godzinny przebieg porównania.
 */
export function useRepairPreview(
	idfirms: readonly number[],
	scope: string | undefined,
	enabled: boolean,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.repairPreview(selectedTenantId, idfirms, scope),
		queryFn: () => agentSyncApi.getRepairPreview(idfirms, scope),
		enabled: enabled && idfirms.length > 0,
		staleTime: REPAIR_PREVIEW_POLL_MS,
		refetchInterval: (q) =>
			// Do kroku „sprawdzona”: zlecone czekają na Robota, naprawione — na pomiar miesiąca.
			q.state.data?.pharmacies.some((p) =>
				p.items.some((i) => i.state === "queued" || i.state === "repaired"),
			)
				? REPAIR_PREVIEW_POLL_MS
				: false,
	});
}

/**
 * Zlecenie napraw (dialog, karta, szczegóły dnia — jedna ścieżka). Po odpowiedzi
 * odświeża karty i dni zleconych aptek (stan zlecenia z backlogu, D64), listę
 * (kolumna „Naprawa”) i podgląd — także gdy część pozycji odrzucono: widok
 * pokazał je na nieaktualnych danych.
 */
export function useRequestRepairs(scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (items: readonly RepairTarget[]) => agentSyncApi.requestRepairs(items, scope),
		onSettled: (_data, _error, items) => {
			// Lista raz, nie per apteka: każde unieważnienie startuje nowe pobranie.
			for (const idfirm of new Set(items.map((i) => i.idfirm))) {
				invalidatePharmacySync(queryClient, idfirm, { withList: false });
			}
			invalidatePharmacyList(queryClient);
			void queryClient.invalidateQueries({ queryKey: [...AGENT_SYNC_QUERY_KEY, "repair-preview"] });
		},
	});
}

/** Katalog zmienia się tylko z deployem backendu — godzina cache wystarczy. */
export const CATALOG_STALE_MS = 60 * 60_000;

export function useAgentSyncCatalog(enabled: boolean) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.catalog(selectedTenantId),
		queryFn: () => agentSyncApi.getCatalog(),
		enabled,
		staleTime: CATALOG_STALE_MS,
	});
}

export const isTerminalJob = (job: AgentSyncJob | undefined) =>
	job != null && TERMINAL_JOB_STATUSES.includes(job.status);

/**
 * Polling joba z 202 do stanu końcowego: co 2 s, najwyżej 60 prób. Stan końcowy
 * unieważnia kartę apteki (`connector.lastTest*`, strona A po przeliczeniu).
 * `timedOut` = job nie skończył się w limicie — wynik dojdzie do karty później.
 */
export function useAgentSyncJob(idfirm: number, jobId: number | null, scope?: string) {
	const { selectedTenantId } = useTenantSwitcher();
	const queryClient = useQueryClient();
	const queryKey = agentSyncKeys.job(selectedTenantId, jobId ?? 0, scope);
	const query = useQuery({
		queryKey,
		queryFn: async () => {
			const job = await agentSyncApi.getJob(jobId as number, scope);
			if (isTerminalJob(job)) {
				invalidatePharmacySync(queryClient, idfirm);
			}
			return job;
		},
		enabled: jobId != null,
		retry: false,
		refetchInterval: (q) => {
			if (isTerminalJob(q.state.data)) return false;
			if (q.state.dataUpdateCount + q.state.errorUpdateCount >= JOB_POLL_MAX_ATTEMPTS) return false;
			return JOB_POLL_INTERVAL_MS;
		},
	});
	// Licznik prób z cache (wynik `useQuery` go nie wystawia); każdy fetch i tak
	// przerenderowuje obserwatora, więc odczyt jest aktualny.
	const state = queryClient.getQueryState(queryKey);
	const attempts = (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0);
	const terminal = isTerminalJob(query.data);
	return {
		...query,
		terminal,
		timedOut: jobId != null && !terminal && attempts >= JOB_POLL_MAX_ATTEMPTS,
	};
}

/**
 * Bindingi aptek widocznych dla użytkownika — jedno zapytanie na całą listę
 * urządzeń. `tenantId` podaje wołający (kafelek czyta przełącznik organizacji
 * bez rzucania, bo bywa renderowany poza `TenantSwitcherProvider`).
 */
export function useDeviceBindings(enabled: boolean, tenantId: number | null, scope?: string) {
	return useQuery({
		queryKey: agentSyncKeys.deviceBindings(tenantId, scope),
		queryFn: () => agentSyncApi.listDeviceBindings(scope),
		enabled,
		staleTime: DEVICE_BINDINGS_STALE_MS,
	});
}

// ── F1 §7: masowe sprawdzenie zgodności ─────────────────────────────────────

/** Postęp zlecenia w toku: apteka liczy się 2–8 s, odświeżenie co 10 s wystarcza. */
export const MANUAL_CHECKS_POLL_MS = 10_000;
/**
 * Pomiary kosztu to średnie z 3 dni przebiegów — zmieniają się raz na dobę; stan
 * okna (noc / dzień / czekanie do 16:00) zmienia się o 07:00 i 16:00.
 */
const MANUAL_CHECK_COST_STALE_MS = 15 * 60_000;

export const isActiveManualCheck = (c: Pick<ManualCheck, "status">) =>
	c.status === "queued" || c.status === "running";

export function useManualChecks(scope: string | undefined) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.manualChecks(selectedTenantId, scope),
		queryFn: () => agentSyncApi.listManualChecks(scope),
		staleTime: MANUAL_CHECKS_POLL_MS,
		refetchInterval: (q) =>
			q.state.data?.checks.some(isActiveManualCheck) ? MANUAL_CHECKS_POLL_MS : false,
	});
}

export function useManualCheckItems(id: number | null, scope: string | undefined, active: boolean) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.manualCheckItems(selectedTenantId, id ?? 0, scope),
		queryFn: () => agentSyncApi.getManualCheckItems(id as number, scope),
		enabled: id != null,
		staleTime: MANUAL_CHECKS_POLL_MS,
		refetchInterval: active ? MANUAL_CHECKS_POLL_MS : false,
	});
}

export function useManualCheckCost(enabled: boolean) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.manualCheckCost(selectedTenantId),
		queryFn: () => agentSyncApi.getManualCheckCost(),
		enabled,
		staleTime: MANUAL_CHECK_COST_STALE_MS,
	});
}

const invalidateManualChecks = (queryClient: QueryClient) =>
	queryClient.invalidateQueries({ queryKey: [...AGENT_SYNC_QUERY_KEY, "manual-checks"] });

/**
 * Liczba zaległych kontroli dla zlecenia `due` (opcja dialogu). Odpowiedź Robota
 * albo zakończone sprawdzenie zmienia ją w każdej chwili — bez cache, każde
 * otwarcie dialogu pyta od nowa (zapytanie tanie, tylko przy otwartym dialogu).
 */
export function useManualCheckDue(
	input: ManualCheckInput | null,
	scope: string | undefined,
	enabled: boolean,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.manualCheckDue(
			selectedTenantId,
			scope,
			input ?? { tables: [], pharmacies: "all" },
		),
		queryFn: () => agentSyncApi.getManualCheckDue(input as ManualCheckInput, scope),
		enabled: enabled && input != null && input.tables.length > 0,
		staleTime: 0,
	});
}

export function useCreateManualCheck(scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: ManualCheckInput) => agentSyncApi.createManualCheck(input, scope),
		onSuccess: () => invalidateManualChecks(queryClient),
	});
}

export function useCancelManualCheck(scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => agentSyncApi.cancelManualCheck(id, scope),
		onSuccess: () => {
			void invalidateManualChecks(queryClient);
			void queryClient.invalidateQueries({
				queryKey: [...AGENT_SYNC_QUERY_KEY, "manual-check-items"],
			});
		},
	});
}

/**
 * D71 „Od początku synchronizacji”: sprawdzenie S ↔ T jednej apteki od `dateFrom`
 * (dolna granica przeglądu); backend dokłada plan historii A porcjami rocznymi.
 * `due` — zaległe kontrole apteki (bez daty, bez strony A).
 */
export function useStartFromBeginning(idfirm: number, scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: { tables: string[]; dateFrom: string } | { tables: string[]; due: true }) =>
			agentSyncApi.createManualCheck({ ...input, pharmacies: "one", idfirm }, scope),
		onSuccess: () => {
			void invalidateManualChecks(queryClient);
			invalidatePharmacySync(queryClient, idfirm, { withList: false });
		},
	});
}

/** Anulowanie planu historii A — karta odświeża się też po 409 (plan skończył się sam). */
export function useCancelAHistory(idfirm: number, scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => agentSyncApi.cancelAHistory(idfirm, scope),
		onSettled: () => invalidatePharmacySync(queryClient, idfirm, { withList: false }),
	});
}

// ── D72: lista kluczy „Z czego wynika rozjazd?” ──────────────────────────────

/** Zlecenie czeka na aptekę albo liczy się — odświeżane co 5 s. */
export const KEY_CHECK_POLL_MS = 5_000;
/** Lista zleceń bez aktywnego — zmienia się tylko po zleceniu z panelu. */
const KEY_CHECKS_STALE_MS = 60_000;
/** Klucze gotowego zlecenia się nie zmieniają (nowy pomiar = nowe zlecenie). */
const KEY_CHECK_DONE_STALE_MS = 30 * 60_000;

export const isActiveKeyCheck = (c: Pick<KeyCheck, "status"> | null | undefined) =>
	c != null && (c.status === "queued" || c.status === "sent" || c.status === "computing");

/**
 * Ostatnie listy kluczy tabeli apteki — po F5 wynik wraca z serwera. `range` = tylko
 * listy obejmujące zakres (lista miesiąca automatu obsługuje panel dnia).
 */
export function useKeyChecks(
	idfirm: number,
	table: string,
	scope: string | undefined,
	enabled: boolean,
	range?: KeyCheckRange,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.keyChecks(selectedTenantId, idfirm, table, scope, range),
		queryFn: () => agentSyncApi.listKeyChecks(idfirm, table, scope, range),
		enabled: enabled && Number.isFinite(idfirm) && table !== "",
		staleTime: KEY_CHECKS_STALE_MS,
		refetchInterval: (q) => keyChecksPoll(q.state.data?.checks ?? []),
	});
}

/** Czekająca lista automatu czeka na noc albo na wolną aptekę — odświeżanie co minutę. */
const AUTO_QUEUED_POLL_MS = 60_000;

/** Co ile odświeżać listy: w toku i ręczne czekające co 5 s, czekający automat co minutę. */
export function keyChecksPoll(checks: Pick<KeyCheck, "status" | "source">[]): number | false {
	if (checks.some((c) => isActiveKeyCheck(c) && !(c.status === "queued" && c.source === "auto")))
		return KEY_CHECK_POLL_MS;
	return checks.some(isActiveKeyCheck) ? AUTO_QUEUED_POLL_MS : false;
}

/** Klucze gotowego zlecenia (`done`), opcjonalnie jednej klasy (filtr po stronie serwera). */
export function useKeyCheckItems(
	idfirm: number,
	id: number | null,
	cls: KeyClass | null,
	scope: string | undefined,
	enabled: boolean,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.keyCheck(selectedTenantId, idfirm, id ?? 0, cls, scope),
		queryFn: () => agentSyncApi.getKeyCheck(idfirm, id as number, cls, scope),
		enabled: enabled && id != null,
		staleTime: KEY_CHECK_DONE_STALE_MS,
	});
}

const invalidateKeyChecks = (queryClient: QueryClient, idfirm: number) =>
	queryClient.invalidateQueries({
		predicate: (q) =>
			keyPart(q.queryKey, 0) === AGENT_SYNC_QUERY_KEY[0] &&
			keyPart(q.queryKey, 1) === "key-checks" &&
			keyPart(q.queryKey, 3) === idfirm,
	});

/**
 * Zlecenie listy kluczy. Lista odświeża się także po 409 — `key_check_running`
 * znaczy, że apteka liczy już inne zlecenie, które lista pokaże.
 */
export function useCreateKeyCheck(idfirm: number, scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: KeyCheckInput) => agentSyncApi.createKeyCheck(idfirm, input, scope),
		onSettled: () => invalidateKeyChecks(queryClient, idfirm),
	});
}

/** Anulowanie zlecenia w toku — lista odświeża się też po 409 (zlecenie skończyło się samo). */
export function useCancelKeyCheck(idfirm: number, scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => agentSyncApi.cancelKeyCheck(idfirm, id, scope),
		onSettled: () => invalidateKeyChecks(queryClient, idfirm),
	});
}

// ── F3: poziom 3 — wartości kolumn A/S/T ─────────────────────────────────────

/** Gotowy poziom 3 się nie zmienia (TTL 24 h, nowy pomiar = nowe zlecenie). */
const LEVEL3_DONE_STALE_MS = 30 * 60_000;

export const isActiveLevel3 = (l: Pick<Level3, "status"> | null | undefined) =>
	l != null && (l.status === "queued" || l.status === "sent" || l.status === "computing");

/** Co ile odświeżać: ręczne w toku co 5 s, czekający automat co minutę, gotowe — wcale. */
export function level3Poll(
	l: Pick<Level3, "status" | "source"> | null | undefined,
): number | false {
	if (!isActiveLevel3(l)) return false;
	return l?.status === "queued" && l.source === "auto" ? AUTO_QUEUED_POLL_MS : KEY_CHECK_POLL_MS;
}

/** Odświeżanie listy zleceń: najczęstsze z jej zleceń w toku. */
export function level3ListPoll(
	items: Pick<Level3, "status" | "source">[] | undefined,
): number | false {
	const polls = (items ?? []).map(level3Poll).filter((p): p is number => p !== false);
	return polls.length ? Math.min(...polls) : false;
}

/** Żywe poziomy 3 listy kluczy `checkId` (po liście automat zleca go sam). */
export function useLevel3(
	idfirm: number,
	checkId: number | null,
	scope: string | undefined,
	enabled: boolean,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.level3(selectedTenantId, idfirm, checkId, scope),
		queryFn: () => agentSyncApi.latestLevel3(idfirm, checkId, scope),
		enabled: enabled && Number.isFinite(idfirm),
		staleTime: (q) => (q.state.data?.items.some(isActiveLevel3) ? 0 : LEVEL3_DONE_STALE_MS),
		refetchInterval: (q) => level3ListPoll(q.state.data?.items),
	});
}

/** Gotowy poziom 3 uzupełnia kolumny różne w kluczach listy — odśwież je. */
export function invalidateKeyCheckItems(queryClient: QueryClient, idfirm: number) {
	return queryClient.invalidateQueries({
		predicate: (q) =>
			keyPart(q.queryKey, 0) === AGENT_SYNC_QUERY_KEY[0] &&
			keyPart(q.queryKey, 1) === "key-check" &&
			keyPart(q.queryKey, 3) === idfirm,
	});
}

/** Ręczne zlecenie poziomu 3 (≤ 50 kluczy) — 409 `level3_running` pokaże zlecenie w toku. */
export function useCreateLevel3(idfirm: number, scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: Level3Input) => agentSyncApi.createLevel3(idfirm, input, scope),
		onSettled: () => invalidateLevel3(queryClient, idfirm),
	});
}

/** Anulowanie poziomu 3 przed wynikiem apteki. */
export function useCancelLevel3(idfirm: number, scope: string | undefined) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => agentSyncApi.cancelLevel3(idfirm, id, scope),
		onSettled: () => invalidateLevel3(queryClient, idfirm),
	});
}

function invalidateLevel3(queryClient: QueryClient, idfirm: number) {
	return queryClient.invalidateQueries({
		predicate: (q) =>
			keyPart(q.queryKey, 0) === AGENT_SYNC_QUERY_KEY[0] &&
			keyPart(q.queryKey, 1) === "level3" &&
			keyPart(q.queryKey, 3) === idfirm,
	});
}

/** SQL weryfikacji — pobierany dopiero po otwarciu dialogu (`enabled`). */
export function useVerifySql(
	idfirm: number,
	params: VerifySqlParams,
	scope: string | undefined,
	enabled: boolean,
) {
	const { selectedTenantId } = useTenantSwitcher();
	return useQuery({
		queryKey: agentSyncKeys.verifySql(selectedTenantId, idfirm, params, scope),
		queryFn: () => agentSyncApi.getVerifySql(idfirm, params, scope),
		enabled: enabled && Number.isFinite(idfirm),
		// Liczby kontroli i lista kluczy zmieniają się z przebiegiem albo nowym zleceniem D72.
		staleTime: KEY_CHECKS_STALE_MS,
	});
}
